// client/src/admin/instagram/InstagramSettings.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Instagram as InstagramIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Link as LinkIcon,
  Token as TokenIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const lang_defaults = {
  instagramSettings: "Instagram Settings",
  instagramSettingsSubtitle:
    "Configure your Instagram account for DM automation and follow-to-earn",
  accountId: "Instagram Account ID",
  accessToken: "Access Token",
  verifyToken: "Webhook Verify Token",
  pageName: "Instagram Page / Username",
  dailyTokenReward: "Daily Token Reward (for followers)",
  webhookUrl: "Webhook URL",
  webhookUrlHint:
    "Copy this URL and paste it in your Meta App webhook settings",
  save: "Save Settings",
  copied: "Copied!",
  copy: "Copy",
  accountIdHint:
    "Your Instagram Business Account ID from Meta Developer Console",
  accessTokenHint:
    "Page Access Token with instagram_manage_messages permission",
  verifyTokenHint:
    "A random string you set — must match what you enter in Meta webhook config",
  dailyTokenRewardHint:
    "Tokens per day granted to users who follow and get approved",
};

const InstagramSettings = ({ lang: propLang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || lang_defaults[k] || k;

  const [form, setForm] = useState({
    account_id: "",
    access_token: "",
    verify_token: "",
    page_name: "",
    daily_token_reward: 100,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    const res = await hitAxios({
      path: "/api/instagram/admin/settings",
      admin: true,
      post: false,
      showLoading: false,
    });
    if (res?.data?.success) {
      const d = res.data.data || {};
      setForm({
        account_id: d.account_id || "",
        access_token: d.access_token || "",
        verify_token: d.verify_token || "",
        page_name: d.page_name || "",
        daily_token_reward: d.daily_token_reward || 100,
      });
      setWebhookUrl(res.data.webhookUrl || "");
    }
  }

  async function handleSave() {
    setLoading(true);
    await hitAxios({
      path: "/api/instagram/admin/settings/save",
      admin: true,
      post: true,
      obj: form,
    });
    setLoading(false);
  }

  function handleCopy() {
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const isDark = theme.palette.mode === "dark";

  return (
    <Box>
      <PageHeader
        title={t("instagramSettings")}
        subtitle={t("instagramSettingsSubtitle")}
        icon={InstagramIcon}
      />

      <Stack spacing={3}>
        {/* Webhook URL Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.secondary.main, 0.04)})`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
            <LinkIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {t("webhookUrl")}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1.5}
          >
            {t("webhookUrlHint")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                fontFamily: "monospace",
                fontSize: "0.78rem",
                color: "text.secondary",
                wordBreak: "break-all",
              }}
            >
              {webhookUrl || "Save settings to generate webhook URL"}
            </Box>
            <Tooltip title={copied ? t("copied") : t("copy")}>
              <IconButton
                onClick={handleCopy}
                disabled={!webhookUrl}
                sx={{
                  bgcolor: copied ? "success.main" : "primary.main",
                  color: "white",
                  borderRadius: 1.5,
                  width: 38,
                  height: 38,
                  flexShrink: 0,
                  "&:hover": {
                    bgcolor: copied ? "success.dark" : "primary.dark",
                  },
                  "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                }}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: 16, color: "#000" }} />
                ) : (
                  <CopyIcon
                    sx={{ fontSize: 16, color: isDark ? "#000" : "#fff" }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Settings Form */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="text.primary"
            mb={2.5}
          >
            Account Configuration
          </Typography>

          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label={t("pageName")}
              placeholder="codeyon_itservices"
              value={form.page_name}
              onChange={(e) =>
                setForm((p) => ({ ...p, page_name: e.target.value }))
              }
              helperText="Your Instagram username (without @)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      sx={{ color: "text.disabled", fontSize: "0.9rem" }}
                    >
                      @
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={t("accountId")}
              placeholder="17841400000000000"
              value={form.account_id}
              onChange={(e) =>
                setForm((p) => ({ ...p, account_id: e.target.value }))
              }
              helperText={t("accountIdHint")}
            />

            <TextField
              fullWidth
              label={t("accessToken")}
              placeholder="EAAxxxxxxxx..."
              value={form.access_token}
              onChange={(e) =>
                setForm((p) => ({ ...p, access_token: e.target.value }))
              }
              type={showToken ? "text" : "password"}
              helperText={t("accessTokenHint")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowToken((p) => !p)}
                      edge="end"
                      size="small"
                      sx={{ color: "text.disabled" }}
                    >
                      {showToken ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={t("verifyToken")}
              placeholder="my_random_verify_token_123"
              value={form.verify_token}
              onChange={(e) =>
                setForm((p) => ({ ...p, verify_token: e.target.value }))
              }
              helperText={t("verifyTokenHint")}
            />

            <Divider />

            <Stack direction="row" alignItems="center" spacing={2}>
              <TokenIcon sx={{ color: "warning.main", fontSize: 20 }} />
              <Box flex={1}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color="text.primary"
                >
                  {t("dailyTokenReward")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("dailyTokenRewardHint")}
                </Typography>
              </Box>
              <TextField
                size="small"
                type="number"
                value={form.daily_token_reward}
                onChange={(e) =>
                  setForm((p) => ({ ...p, daily_token_reward: e.target.value }))
                }
                inputProps={{
                  min: 1,
                  style: { width: 80, textAlign: "center" },
                }}
                sx={{ width: 110 }}
              />
            </Stack>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {t("save")}
            </Button>
          </Stack>
        </Paper>

        {/* Setup Guide */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, isDark ? 0.06 : 0.04),
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="warning.main"
            mb={1.5}
          >
            📋 Setup Guide
          </Typography>
          <Stack spacing={1}>
            {[
              "1. Go to developers.facebook.com and create/open your app",
              "2. Add Instagram product and connect your Instagram Business account",
              "3. Generate a Page Access Token with instagram_manage_messages permission",
              "4. Copy the Webhook URL above and paste it in the Webhooks section",
              "5. Set your Verify Token (same value as above) in Meta webhook config",
              "6. Subscribe to messages webhook field",
              "7. Save settings here and test by sending a DM to your Instagram",
            ].map((step, i) => (
              <Typography
                key={i}
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.7 }}
              >
                {step}
              </Typography>
            ))}
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default InstagramSettings;
